package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LCheckIcon
import dev.lingvo.site.ds.LCloseIcon
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transitionColors
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import dev.lingvo.site.i18n.t

/**
 * The band that argues against itself.
 *
 * Every other band on this page is the case for moving. This one is the case for not, set at the
 * same size and given the same furniture, because a page that only says yes is a page a buyer has
 * to fact-check. Naming Crowdin, Lokalise and Weblate as the right answer to four specific needs
 * costs four prospects who were never going to stay, and buys the other eight the belief that the
 * rest of the page was written the same way.
 *
 * **The eight lines are load-bearing and specific.** "Forty-odd integrations", "over-the-air string
 * updates" and "self-hosted and free" are capabilities those three products genuinely ship and this
 * one genuinely does not. Rewriting any of them into something vaguer — "if you need deep
 * integrations" — turns an honest column into a straw man, which is worse than not having the
 * column at all.
 *
 * **The two columns are told apart by shape, never by tint.** Green-good / red-bad carries nothing
 * for the ~8% of men with a red-green deficiency, and nothing at all in a greyscale print or a
 * high-contrast forced-colours mode. A tick and a cross are different silhouettes at any size, and
 * the column headings say the same thing in words on top of that — which is what the glyphs, being
 * `aria-hidden`, rely on.
 */
@Composable
fun LimitsBand() {
    Band(ground = BandGround.Paper, labelledBy = LIMITS_TITLE_ID) {
        // The label and heading belong to each other more closely than to the columns below, so
        // they are grouped rather than left to the band's own 32px rhythm.
        Div(LimitsHeadStyle.toModifier().toAttrs()) {
            // Sentence case in the DOM; `BandLabelStyle` supplies the capitals. A screen reader
            // given literal capitals reads them as a shout or spells them out letter by letter,
            // which is the rule `FormatsBand` and `HeroPitch` both write down.
            BandLabel(t("marketing.limits_band.before_you_move_a_single_file", "Before you move a single file"))
            BandHeading(t("marketing.limits_band.two_honest_columns", "Two honest columns."), id = LIMITS_TITLE_ID)
        }

        Div(LimitsColumnsStyle.toModifier().toAttrs()) {
            LimitsColumn(
                titleId = SWITCH_TITLE_ID,
                title = t("marketing.limits_band.switch_to_us", "Switch to us"),
                rows = SWITCH_ROWS,
                // Sunken, so the recommendation reads as the well the eye falls into and the
                // alternative reads as the page it is already standing on. That is the whole of the
                // emphasis: no accent fill, no border weight, no leading edge marker.
                columnModifier = LimitsSwitchColumnStyle.toModifier(),
            ) { modifier -> LCheckIcon(modifier) }

            LimitsColumn(
                titleId = STAY_TITLE_ID,
                title = t("marketing.limits_band.stay_where_you_are", "Stay where you are"),
                rows = STAY_ROWS,
                columnModifier = LimitsStayColumnStyle.toModifier(),
            ) { modifier -> LCloseIcon(modifier) }
        }
    }
}

/**
 * One bordered column: a header that states the verdict, then the four cases it covers.
 *
 * The rows are a `<ul>` because they are one — four unordered peers under a title. The title itself
 * is a `<span>` and not an `<h3>`: this page carries exactly one heading level per band by design,
 * and `aria-labelledby` gives the list its accessible name without inventing a second tier of
 * document outline for a screen-reader user to navigate through.
 */
@Composable
private fun LimitsColumn(
    titleId: String,
    title: String,
    rows: List<LimitRow>,
    columnModifier: Modifier,
    glyph: @Composable (Modifier) -> Unit,
) {
    Div(LimitsColumnStyle.toModifier().then(columnModifier).toAttrs()) {
        Div(LimitsColumnHeadStyle.toModifier().toAttrs()) {
            // The icon ladder's largest rung — the same box `LButtonSize.LG` gives an icon —
            // rather than a size invented to sit beside this particular heading. Written as two raw
            // `20.px` modifiers before, which is the pair `iconSize` exists to keep in agreement.
            Span(LimitsGlyphStyle.toModifier().toAttrs()) {
                glyph(Modifier.iconSize(IconSize.XL))
            }
            Span(LimitsColumnTitleStyle.toModifier().toAttrs { attr("id", titleId) }) { Text(title) }
        }

        Ul(
            LimitsRowsStyle.toModifier().toAttrs {
                attr("aria-labelledby", titleId)
                // `list-style: none` drops the list role in Safari's VoiceOver, so a screen reader
                // there would hear four sentences with nothing saying how many there are or that
                // they are peers. Restoring the role costs an attribute and restores the count.
                attr("role", "list")
            },
        ) {
            rows.forEach { row ->
                Li(LimitsRowStyle.toModifier().toAttrs()) {
                    Div(LimitsRowTextStyle.toModifier().toAttrs()) {
                        Div(LimitsRowLeadStyle.toModifier().toAttrs()) {
                            Text(t("marketing.limits_band.row.${row.key}.lead", row.lead))
                        }
                        Div(LimitsRowNoteStyle.toModifier().toAttrs()) {
                            Text(t("marketing.limits_band.row.${row.key}.note", row.note))
                        }
                    }
                    // Trailing, not leading. A glyph in the left margin of every row would build the
                    // coloured rail this system does not allow, and would push eight lines of prose
                    // off their shared left edge for a mark that repeats the column heading.
                    Span(LimitsGlyphStyle.toModifier().toAttrs()) {
                        glyph(Modifier.iconSize(IconSize.MD))
                    }
                }
            }
        }
    }
}

/**
 * A case for or against, and the one-line reason underneath it.
 *
 * [key] names the catalogue entry. The two rows below are top-level `val`s evaluated at class-init,
 * before any catalogue is fetched, so a lookup written into them would return its own English
 * fallback for the life of the page — the same reason the nav model and the price card are
 * translated by id at the point they are drawn.
 */
private data class LimitRow(val key: String, val lead: String, val note: String)

private val SWITCH_ROWS = listOf(
    LimitRow("locales", "You ship three or more locales", "plurals start biting at the third"),
    LimitRow("in_repo", "Your strings live in the repo", "eleven formats, round-tripped"),
    LimitRow("translators", "Translators outnumber admins", "they are free, always"),
    LimitRow("ci_gate", "You want CI to block a bad locale", "one command, non-zero exit"),
)

private val STAY_ROWS = listOf(
    LimitRow("integrations", "You need forty-odd integrations", "Crowdin has them, we do not"),
    LimitRow("ota", "You need over-the-air string updates", "Lokalise ships that, we do not"),
    LimitRow("tiny", "You are on one language, forty keys", "a spreadsheet is genuinely fine"),
    // Was "You want it self-hosted and free" — which was not true about us. Self-hosted Lingvo is
    // free and uncapped forever (`docs/PRICING.md`), so that row sent away people this product
    // actually serves, and did it by conceding a fact we hold. What Weblate genuinely has and we do
    // not is a licence: ours is undecided, and `PRODUCT.md` is explicit that nothing may state or
    // imply one until it is. That is the honest line, and it is narrower than the one it replaces.
    LimitRow("licence", "You need an open-source licence", "Weblate is GPL, ours is undecided"),
)

private const val LIMITS_TITLE_ID = "limits-title"
private const val SWITCH_TITLE_ID = "limits-switch"
private const val STAY_TITLE_ID = "limits-stay"

/**
 * The tint a row takes under the cursor, set by the column rather than by the row.
 *
 * A single hover colour cannot serve both columns: they sit on different grounds, so one value is
 * either invisible on the sunken side or a flash on the paper side. A variable set once per column
 * keeps one hover rule for both, which is what stops the two sides drifting into two behaviours.
 */
private val LimitsRowTintVar by StyleVariable<CSSColorValue>(prefix = "lingvo")

/** The verdict tone, set per column for the same reason as [LimitsRowTintVar]: one rule, two values. */
private val LimitsGlyphTintVar by StyleVariable<CSSColorValue>(prefix = "lingvo")

val LimitsHeadStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .gap(Space.LG)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
        }
}

val LimitsColumnsStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .gap(Space.XL)
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "minmax(0, 1fr)")
                // `start`, so a column with shorter lines keeps its own height instead of being
                // stretched to match its neighbour and growing four hollow rows.
                property("align-items", "start")
            }
    }
    // Two columns of eight-word lines need roughly 340px each before the lead lines start wrapping
    // to three. Below MD they stack, and stacking is the honest failure — shrinking the type to
    // keep them side by side would make the small print smaller than Micro.
    Breakpoint.MD {
        Modifier.styleModifier { property("grid-template-columns", "repeat(2, minmax(0, 1fr))") }
    }
}

val LimitsColumnStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .borderRadius(Radius.Modal)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            // Without the clip the header's hairline and the row tints run straight through the
            // corner radius, which reads as a rendering fault rather than as a rounded box.
            property("overflow", "hidden")
        }
}

/** The recommendation. Sunken ground; hover lifts a row one step towards paper. */
val LimitsSwitchColumnStyle = CssStyle.base {
    Modifier
        .backgroundColor(colorMode.toSitePalette().surfaceSunken)
        .setVariable(LimitsRowTintVar, colorMode.toSitePalette().surfaceBase)
        .setVariable(LimitsGlyphTintVar, colorMode.toSitePalette().success)
}

/** The alternative. Sits on the band's own paper, so hover goes the other way. */
val LimitsStayColumnStyle = CssStyle.base {
    Modifier
        .backgroundColor(colorMode.toSitePalette().surfaceBase)
        .setVariable(LimitsRowTintVar, colorMode.toSitePalette().surfaceSunken)
        .setVariable(LimitsGlyphTintVar, colorMode.toSitePalette().danger)
}

val LimitsColumnHeadStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .gap(Space.MD)
        .padding(Space.XL)
        .borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .styleModifier {
            property("display", "flex")
            property("align-items", "center")
        }
}

val LimitsColumnTitleStyle = CssStyle.base {
    Modifier
        .displayFont()
        .fontSize(TypeScale.HeadingSmall)
        .fontWeight(700)
        .letterSpacing((-0.5).px)
        .color(colorMode.toSitePalette().textPrimary)
}

/**
 * The tick and the cross.
 *
 * Both glyphs inherit `currentColor`, so the tone is set here once per column and the SVG stays a
 * shape. `success` and `danger` are the tokens rather than the accent because these are verdicts,
 * not emphasis — but nothing in the band depends on a reader telling green from red.
 */
val LimitsGlyphStyle = CssStyle.base {
    Modifier
        .color(LimitsGlyphTintVar.value())
        .styleModifier {
            property("display", "inline-flex")
            property("flex", "0 0 auto")
        }
}

val LimitsRowsStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .margin(0.px)
        .padding(0.px)
        .styleModifier { property("list-style", "none") }
}

/**
 * A row, and the only thing in this band that moves.
 *
 * Tint only, at [dev.lingvo.site.ds.Motion.FastMs] — no cursor change, no lift, no border. A row
 * here is prose, not a control, and a lift or a pointer would promise a click that does not exist.
 * What the tint is actually for is tracking: eight rows of two lines each is enough for the eye to
 * lose which note belongs to which case.
 */
val LimitsRowStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .gap(Space.LG)
            .padding(topBottom = Space.LG, leftRight = Space.XL)
            .borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .transitionColors()
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
            }
    }
    // The column already draws its own bottom edge; a fourth divider would double it.
    cssRule(":last-child") {
        Modifier.styleModifier { property("border-bottom-width", "0") }
    }
    hover {
        Modifier.backgroundColor(LimitsRowTintVar.value())
    }
}

val LimitsRowTextStyle = CssStyle.base {
    Modifier
        .gap(Space.XS)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("flex", "1 1 auto")
            // Flex items floor at their content width, so a long unbroken lead line would push the
            // glyph out of the column instead of wrapping.
            property("min-width", "0")
        }
}

val LimitsRowLeadStyle = CssStyle.base {
    Modifier
        .fontSize(TypeScale.BodySmall)
        .fontWeight(600)
        .lineHeight(TypeScale.BodyLineHeight)
        .color(colorMode.toSitePalette().textPrimary)
}

/**
 * The reason, in mono.
 *
 * Mono because these are the concrete claims — format counts, exit codes, competitor names — and
 * the page's mono face is where it puts anything a reader could check. `textMuted` and not the
 * design file's own `#948C7E`: that colour measures 3.00:1 on paper and 2.72:1 on the sunken
 * ground, so eight lines of the band's most specific text would have been the least legible.
 */
val LimitsRowNoteStyle = CssStyle.base {
    Modifier
        .monoFont()
        .fontSize(TypeScale.Micro)
        .lineHeight(TypeScale.BodyLineHeight)
        .color(colorMode.toSitePalette().textMuted)
}
